import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import querys from 'services/querys';
import useAlert from 'utils/alert';
import { GQLDeleteAssociation } from '../services/mutations';
import { FormValueAssociation } from '../components/PartyModalAssociations';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'utils/helpers';

const useAssociation = (handleFilterParties) => {
  const { partyId, id: orderId } = useParams<{
    id: string;
    partyId: string;
    kind: string;
  }>();
  const showAlert = useAlert();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const queryCache = useQueryClient();

  const { mutate: createAssociation, ...responseCreateAssociation } = useMutation(
    querys.createAssociation,
    {
      onSuccess: (data) => {
        queryCache.invalidateQueries(['party-order', orderId]);
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        const { parties } = data.createOrderAssociation;
        handleFilterParties(parties);
        setOpen(false);
        responseCreateAssociation.reset();
      },
    }
  );

  const { mutate: deleteAssociation, ...responseDeleteAssociation } = useMutation(
    GQLDeleteAssociation,
    {
      onSuccess: (data) => {
        queryCache.invalidateQueries(['party-order', orderId]);
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        const { parties } = data.deleteOrderAssociation;
        handleFilterParties(parties);

        responseDeleteAssociation.reset();
      },
    }
  );

  const handleDeleteAssociation = (
    partyIdAssociation: string,
    kindAssociation: string,
    onSuccess?: () => void
  ) => {
    deleteAssociation(
      {
        id: orderId!,
        partyId: partyIdAssociation,
        kind: kindAssociation,
        linkedToId: partyId,
      },
      {
        onSuccess: () => onSuccess?.(),
      }
    );
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitAssociation = (data: FormValueAssociation) => {
    const { partyAssociationId, role, kind } = data;

    createAssociation({
      id: orderId!,
      partyId: partyAssociationId,
      kind: role,
      linkedToId: partyId,
      linkedToKind: kind,
    });
  };

  return {
    open,
    isCreating: responseCreateAssociation.isLoading,
    isDeleting: responseDeleteAssociation.isLoading,
    handleOpen,
    handleClose,
    handleSubmitAssociation,
    handleDeleteAssociation,
  };
};

export default useAssociation;
